import { memo, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { type Color } from '@air/api/types';

import { SingleColorFilter } from '~/components/Filters/ColorFilter/SingleColorFilter';
import { getAppliedParamsCount } from '~/components/Filters/FiltersBar/filtersBarUtils';
import { FilterTrigger } from '~/components/Filters/SingleFilters/FilterTrigger';
import { type SingleFilterProps } from '~/components/Filters/types';
import { baseFiltersOptions } from '~/components/FiltersMenu/types';
import { useFilterParams } from '~/hooks/filters/useFilterParams';
import { removeFilterAction } from '~/store/filters/actions';
import { type SelectedFilter } from '~/store/filters/types';

export type ColorFilterTriggerProps = {
  colors: Color[];
} & SingleFilterProps;

export const ColorFilterTrigger = memo(
  ({ onApplyClick, colors, onRemoveClick, onOpenChange }: ColorFilterTriggerProps) => {
    const dispatch = useDispatch();
    const { colorsParam } = useFilterParams();

    const content = useMemo(
      () => <SingleColorFilter colors={colors} onApplyClick={onApplyClick} />,
      [colors, onApplyClick],
    );

    const appliedColorsCount = getAppliedParamsCount(colorsParam);

    const removeFilter = useCallback(() => {
      const filter: SelectedFilter = { type: 'color' };
      dispatch(removeFilterAction({ filter }));
      onRemoveClick?.([filter]);
    }, [dispatch, onRemoveClick]);

    return (
      <FilterTrigger
        onRemoveClick={removeFilter}
        count={appliedColorsCount}
        filterContent={content}
        title={baseFiltersOptions['color'].label}
        Icon={baseFiltersOptions['color'].icon}
        onOpenChange={onOpenChange}
      />
    );
  },
);

ColorFilterTrigger.displayName = 'ColorFilterTrigger';
